import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { apiLogin, apiSignup, apiConvertGoogleToken, apiResendVerification, apiSubmitVerification, apiUpdateProfile, apiUploadProfilePicture, apiCreateAddress, apiUpdateAddress, apiDeleteAddress, apiRevokeToken, fetchCurrentUser, fetchUserStores, fetchStoreBySlug, apiCreateStore, clearTokens, getStoredToken } from '../services/api';
import { clearChannelsCache } from '../services/ordersApi';
import { clearSheetTokens } from '../services/googleSheetAuth';
import {
  mapSubDomain,
  pickStoreCustomDomain,
  pickStoreCustomHost,
  resolveStoreCustomDomain,
  resolveStoreDefaultHost,
  resolveStoreDomain,
} from '../utils/storeDomain';

export { resolveStoreDomain };

/** Clears all app data from browser storage on logout. */
function clearAllAppData() {
  localStorage.clear();
  sessionStorage.clear();
}

const AuthContext = createContext(null);

const API_STORES_KEY = 'kio_api_stores';
const PICKING_STORE_KEY = 'kio_picking_store';

const SESSION_HYDRATED_KEY = 'kio_session_hydrated';

function hasCachedSession() {
  try {
    const raw = localStorage.getItem('auth_user');
    return Boolean(raw && raw !== 'null');
  } catch {
    return false;
  }
}

function isSessionHydrated() {
  return sessionStorage.getItem(SESSION_HYDRATED_KEY) === '1';
}

function markSessionHydrated() {
  sessionStorage.setItem(SESSION_HYDRATED_KEY, '1');
}

function clearSessionHydrated() {
  sessionStorage.removeItem(SESSION_HYDRATED_KEY);
}

if (typeof window !== 'undefined' && getStoredToken() && hasCachedSession()) {
  markSessionHydrated();
}

function setPickingStore(active) {
  if (active) sessionStorage.setItem(PICKING_STORE_KEY, '1');
  else sessionStorage.removeItem(PICKING_STORE_KEY);
}

function isPickingStore() {
  return sessionStorage.getItem(PICKING_STORE_KEY) === '1';
}

function normalizeGeoRef(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return value;
  return { id: value };
}

function mapApiAddress(a) {
  return {
    id: a.id,
    name: a.name,
    street: a.street,
    street2: a.street2,
    city: normalizeGeoRef(a.city),
    state: normalizeGeoRef(a.state),
    country: normalizeGeoRef(a.country),
    default: Boolean(a.default),
    owner: a.owner,
  };
}

function mapApiConfirmation(c) {
  if (!c || typeof c !== 'object') return null;
  return {
    lastPhoneNumber: c.last_phone_number ?? null,
    lastEmail: c.last_email ?? null,
    emailConfirmed: Boolean(c.email_confirmed),
    phoneConfirmed: Boolean(c.phone_confirmed),
  };
}

function detectBetaUser(u) {
  if (!u || typeof u !== 'object') return false;

  if (u.beta === true || u.is_beta === true || u.isBeta === true) return true;

  const candidates = [
    u.user_type,
    u.userType,
    u.account_type,
    u.accountType,
    u.type,
    u.role,
    u.status,
    u.subscription?.pack?.name,
    u.subscription?.pack?.slug,
    u.subscription?.pack_name,
    u.subscription?.name,
  ];

  return candidates.some((value) => String(value || '').trim().toLowerCase() === 'beta');
}

/** Contact en attente de confirmation après changement d'e-mail ou de téléphone. */
export function getPendingContactVerification(user) {
  const c = user?.confirmation;
  if (!c) return null;
  if (c.lastPhoneNumber && !c.phoneConfirmed) {
    return { contactMode: 'phone', contact: c.lastPhoneNumber };
  }
  if (c.lastEmail && !c.emailConfirmed) {
    return { contactMode: 'email', contact: c.lastEmail };
  }
  return null;
}

export function needsAccountVerification(user) {
  if (!user) return false;
  return !user.isConfirmed;
}

/**
 * Signed-in account with no phone at all (typically a Google sign-up): neither a
 * verified number nor one pending its SMS code. kio-api only copies a new number
 * to phone_number once the code is verified, so a pending one counts as given.
 */
export function needsPhoneNumber(user) {
  if (!user?.isConfirmed) return false;
  return !user.phone && !user.confirmation?.lastPhoneNumber;
}

function mapApiUser(u) {
  return {
    uid: u.uid,
    email: u.email,
    username: u.username,
    firstName: u.first_name,
    lastName: u.last_name,
    phone: u.phone_number,
    fullName: u.full_name,
    avatar: u.avatar_src,
    color: u.color,
    isBeta: detectBetaUser(u),
    subscription: u.subscription,
    // Accès KIOAGENT résolu par kio-api (pour un staff : celui du propriétaire de la boutique).
    kioagent: u.kioagent === true,
    isConfirmed: Boolean(u.isconfirmed ?? u.is_confirmed),
    confirmation: mapApiConfirmation(u.confirmation),
    addresses: Array.isArray(u.addresses) ? u.addresses.map(mapApiAddress) : [],
    // Staff memberships: one entry per store. Used to attribute orders to the
    // current staff (order_served_by). Empty for a plain user / store owner.
    staff: Array.isArray(u.staff)
      ? u.staff.map((s) => ({
          uid: s.uid,
          storeSlug: s.store_slug ?? s.storeSlug,
          role: s.role_name ?? s.role,
          permissions: s.permissions ?? s.web_permissions ?? {},
        }))
      : [],
  };
}

const HONEYPOT_SETTING_KEYS = [
  'honeypot_phone_max',
  'honeypot_ip_max',
  'honeypot_device_max',
  'honeypot_window',
];

function mapStoreSettings(s) {
  const settings = s?.settings ?? s ?? {};
  const mapped = {
    custom_checkout: Boolean(settings.custom_checkout),
    cart_enabled: Boolean(settings.cart_enabled),
    delivery: Boolean(settings.delivery),
    whatsapp: Boolean(settings.whatsapp),
  };
  // COD Shield (honeypot) values live on the same settings resource but are
  // numbers, not booleans — carry them through so they survive a reload.
  for (const key of HONEYPOT_SETTING_KEYS) {
    if (settings[key] != null) mapped[key] = settings[key];
  }
  return mapped;
}

function normalizeStoreGeoRef(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') {
    return {
      id: value.id,
      name: value.name ?? value.country_name ?? value.state_name ?? value.city_name ?? '',
    };
  }
  return { id: value, name: '' };
}

function mapApiStore(s) {
  const source = s || {};
  const rawLanguage = source.language;
  const languageArray = Array.isArray(rawLanguage)
    ? rawLanguage.filter(Boolean).map((code) => String(code))
    : (rawLanguage ? [String(rawLanguage)] : []);
  const defaultLanguage = languageArray[0] || 'ar';
  const subDomain = mapSubDomain(source);
  const hasRawCustom = Object.prototype.hasOwnProperty.call(source, 'custom_domain');
  // When the payload carries custom_domain (store detail, local patches) it is the
  // truth, even empty: falling back to a cached `customDomain` kept a disconnected
  // domain forever (persisted in localStorage, survived reloads).
  const customDomain = hasRawCustom
    ? pickStoreCustomDomain(source.custom_domain)
    : resolveStoreCustomDomain(source);
  const defaultDomain = resolveStoreDefaultHost({ slug: source.slug, subDomain });

  return {
    id: source.id,
    name: source.name,
    slug: source.slug,
    subDomain,
    ...(hasRawCustom
      ? { custom_domain: source.custom_domain }
      : (customDomain ? { custom_domain: [customDomain] } : {})),
    defaultDomain,
    customDomain,
    domain: customDomain || defaultDomain,
    url: source.url,
    logo: source.logo_src ?? source.logo,
    favicon: source.favicon_src ?? source.favicon ?? '',
    currency: source.default_currency ?? source.currency,
    language: languageArray,
    defaultLanguage,
    industry: source.industry,
    ispublished: source.ispublished,
    description: source.description || '',
    supportPhone: source.support_phone || source.supportPhone || '',
    supportEmail: source.support_email || source.supportEmail || '',
    contactEmail: source.contact_email || source.contactEmail || '',
    senderEmail: source.sender_email || source.senderEmail || source.support_email || source.supportEmail || '',
    street: source.street || '',
    street2: source.street2 || '',
    city: normalizeStoreGeoRef(source.city),
    state: normalizeStoreGeoRef(source.state),
    country: normalizeStoreGeoRef(source.country),
    legalTitle: source.legal_title || source.legalTitle || '',
    legalName: source.legal_name || source.legalName || '',
    tax: source.tax ?? 0,
    vat: source.vat || source.vat_number || source.tva || '',
    owner: source.owner || '',
    plan: source.plan || 'free',
    createdAt: source.createdAt || new Date().toISOString(),
    point_system: Boolean(source.point_system),
    pointValue: parseFloat(source.point_value) || 0,
    settings: mapStoreSettings(source),
  };
}

function mergeStoreDetail(prev, raw) {
  return {
    ...mapApiStore({ ...(prev || {}), ...raw }),
    _detailLoaded: true,
  };
}

function persistCurrentStore(store) {
  localStorage.setItem('kio_current_store', JSON.stringify(store));
  try {
    const list = JSON.parse(localStorage.getItem(API_STORES_KEY) || 'null');
    if (Array.isArray(list)) {
      const idx = list.findIndex((s) => s.id === store.id || s.slug === store.slug);
      if (idx >= 0) {
        list[idx] = { ...list[idx], ...store };
        localStorage.setItem(API_STORES_KEY, JSON.stringify(list));
      }
    }
  } catch { /* ignore */ }
}

function mergeListStoreWithDetail(saved, fresh) {
  const merged = saved?._detailLoaded && saved.slug === fresh.slug
    ? { ...fresh, settings: saved.settings, _detailLoaded: true }
    : { ...fresh, _detailLoaded: false };

  const freshCustom = pickStoreCustomHost(fresh);
  const savedCustom = pickStoreCustomHost(saved);
  const freshHasCustomField = Object.prototype.hasOwnProperty.call(fresh, 'custom_domain');

  // L'API liste omet custom_domain — garder la valeur déjà connue.
  if (!freshHasCustomField) {
    return savedCustom
      ? { ...merged, customDomain: savedCustom, custom_domain: saved.custom_domain ?? [savedCustom], domain: savedCustom }
      : merged;
  }

  // custom_domain présent dans la réponse : c'est la vérité, même vide (domaine déconnecté).
  return {
    ...merged,
    customDomain: freshCustom,
    custom_domain: fresh.custom_domain,
    domain: freshCustom || fresh.defaultDomain || merged.domain,
  };
}

function hydrateStoreFromList(saved) {
  if (!saved) return null;
  try {
    const list = JSON.parse(localStorage.getItem(API_STORES_KEY) || 'null');
    if (!Array.isArray(list)) {
      const base = saved.settings ? saved : { ...saved, settings: mapStoreSettings(saved) };
      return base._detailLoaded ? base : { ...base, _detailLoaded: false };
    }
    const fresh = list.find((s) => s.id === saved.id || s.slug === saved.slug) || saved;
    return mergeListStoreWithDetail(saved, fresh.settings ? fresh : { ...fresh, settings: mapStoreSettings(fresh) });
  } catch {
    const base = saved.settings ? saved : { ...saved, settings: mapStoreSettings(saved) };
    return base._detailLoaded ? base : { ...base, _detailLoaded: false };
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      if (!getStoredToken()) {
        localStorage.removeItem('auth_user');
        return null;
      }
      return JSON.parse(localStorage.getItem('auth_user') || 'null');
    } catch { return null; }
  });

  const [currentStore, setCurrentStoreState] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('kio_current_store') || 'null');
      return hydrateStoreFromList(saved);
    } catch { return null; }
  });

  const getUserStores = useCallback((username) => {
    const apiStores = JSON.parse(localStorage.getItem(API_STORES_KEY) || 'null');
    if (apiStores) return apiStores;
    const all = JSON.parse(localStorage.getItem('kio_stores') || '[]');
    return username ? all.filter(s => s.username === username) : all;
  }, []);

  const loadSession = useCallback(async (includeStores = true, resetStore = false) => {
    const apiUser = await fetchCurrentUser();
    const userData = mapApiUser(apiUser);

    let storeList = [];
    if (includeStores && userData.isConfirmed) {
      const apiStores = await fetchUserStores();
      storeList = Array.isArray(apiStores) ? apiStores.map(mapApiStore) : [];
      localStorage.setItem(API_STORES_KEY, JSON.stringify(storeList));
    } else if (resetStore) {
      localStorage.removeItem(API_STORES_KEY);
    }

    localStorage.setItem('auth_user', JSON.stringify(userData));

    if (resetStore) {
      localStorage.removeItem('kio_current_store');
      setCurrentStoreState(null);
      setPickingStore(true);
    } else if (isPickingStore()) {
      localStorage.removeItem('kio_current_store');
      setCurrentStoreState(null);
    } else {
      let saved = null;
      try {
        saved = JSON.parse(localStorage.getItem('kio_current_store') || 'null');
      } catch { /* ignore */ }

      if (saved && storeList.length > 0) {
        const fresh = storeList.find((s) => s.id === saved.id || s.slug === saved.slug);
        if (fresh) {
          const next = mergeListStoreWithDetail(saved, fresh);
          persistCurrentStore(next);
          setCurrentStoreState(next);
        } else {
          localStorage.removeItem('kio_current_store');
          setCurrentStoreState(null);
        }
      } else if (saved) {
        setCurrentStoreState(saved);
      }
    }

    setUser(userData);
    markSessionHydrated();
    return userData;
  }, []);

  const login = useCallback(async (identifier, password, remember = true) => {
    await apiLogin(identifier, password, remember);
    const userData = await loadSession(true, true);
    return {
      needsVerification: !userData.isConfirmed,
      contact: userData.email || userData.phone,
      contactMode: userData.email ? 'email' : 'phone',
    };
  }, [loadSession]);

  const loginWithGoogle = useCallback(async (googleAccessToken, remember = true) => {
    await apiConvertGoogleToken(googleAccessToken, remember);
    const userData = await loadSession(true, true);
    return {
      needsVerification: !userData.isConfirmed,
      contact: userData.email || userData.phone,
      contactMode: userData.email ? 'email' : 'phone',
    };
  }, [loadSession]);

  // Met à jour la liste boutiques si le cache local date d'avant les champs contact API
  useEffect(() => {
    if (!user?.isConfirmed || !getStoredToken()) return undefined;
    let cancelled = false;

    const syncStoresFromApi = async () => {
      try {
        const cached = JSON.parse(localStorage.getItem(API_STORES_KEY) || 'null');
        const saved = JSON.parse(localStorage.getItem('kio_current_store') || 'null');
        const cacheStale = !Array.isArray(cached) || cached.length === 0
          || !Object.prototype.hasOwnProperty.call(cached[0] || {}, 'supportPhone');
        const storeStale = saved && (
          !Object.prototype.hasOwnProperty.call(saved, 'supportPhone')
          || !Object.prototype.hasOwnProperty.call(saved, 'defaultDomain')
          || !Object.prototype.hasOwnProperty.call(saved, 'custom_domain')
          || (saved.subDomain?.id && !saved.subDomain?.domain_name)
        );
        if (!cacheStale && !storeStale) return;

        const apiStores = await fetchUserStores();
        if (cancelled) return;
        const storeList = Array.isArray(apiStores) ? apiStores.map(mapApiStore) : [];
        localStorage.setItem(API_STORES_KEY, JSON.stringify(storeList));

        if (saved) {
          const fresh = storeList.find((s) => s.id === saved.id || s.slug === saved.slug);
          if (fresh) {
            const next = mergeListStoreWithDetail(saved, fresh);
            persistCurrentStore(next);
            setCurrentStoreState(next);
          }
        }
      } catch { /* ignore */ }
    };

    syncStoresFromApi();
    return () => { cancelled = true; };
  }, [user]);

  // Hydrate API une seule fois par onglet : jamais user/store à chaque navigation
  useEffect(() => {
    if (!getStoredToken()) {
      clearSessionHydrated();
      return undefined;
    }
    if (isSessionHydrated() || hasCachedSession()) {
      if (hasCachedSession()) markSessionHydrated();
      // Render instantly from cache, but refresh in the background so permission
      // changes (e.g. the owner editing this staff) apply on reload without a
      // forced logout/login.
      loadSession(true, false).catch(() => {});
      return undefined;
    }

    let cancelled = false;
    loadSession(true, false).catch(() => {
      if (cancelled) return;
      clearSessionHydrated();
      clearTokens();
      localStorage.removeItem('auth_user');
      localStorage.removeItem('kio_current_store');
      localStorage.removeItem(API_STORES_KEY);
      setPickingStore(false);
      setUser(null);
      setCurrentStoreState(null);
    });
    return () => { cancelled = true; };
  }, [loadSession]);

  // Détail boutique (domaine custom, settings…) — rechargé à chaque sélection de boutique.
  // Ne pas bloquer sur _detailLoaded : la liste /store/ omet souvent custom_domain.
  useEffect(() => {
    const slug = currentStore?.slug;
    if (!slug) return undefined;

    let cancelled = false;
    fetchStoreBySlug(slug)
      .then((data) => {
        if (cancelled) return;
        setCurrentStoreState((prev) => {
          if (!prev || prev.slug !== slug) return prev;
          const next = mergeStoreDetail(prev, data);
          persistCurrentStore(next);
          return next;
        });
      })
      .catch(() => { /* garde les données partielles de la liste */ });

    return () => { cancelled = true; };
  }, [currentStore?.slug]);

  const signup = useCallback(async ({ firstName, lastName, username, email, phone, password, recaptchaToken }) => {
    await apiSignup({
      email: email || null,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      password,
      phoneNumber: phone || null,
      username: username.trim(),
      recaptchaToken,
    });

    const identifier = email || phone || username.trim();
    await apiLogin(identifier, password);
    const userData = await loadSession(false, true);

    return {
      needsVerification: !userData.isConfirmed,
      contact: email || phone || userData.email || userData.phone,
      contactMode: email ? 'email' : 'phone',
    };
  }, [loadSession]);

  const verifyAccount = useCallback(async ({ code, email, phoneNumber, contactMode }) => {
    await apiSubmitVerification({ code, email, phoneNumber, contactMode });
    return loadSession(true, true);
  }, [loadSession]);

  const resendVerificationCode = useCallback(async ({ email, phoneNumber }) => {
    await apiResendVerification({ email, phoneNumber });
  }, []);

  const selectStore = useCallback((store) => {
    const next = { ...mapApiStore(store), _detailLoaded: false };
    persistCurrentStore(next);
    // Channels are per-store; drop the cache so we don't reuse the previous
    // store's channel ids (which 404 on order creation for the new store).
    clearChannelsCache();
    setCurrentStoreState(next);
    setPickingStore(false);
  }, []);

  const refreshCurrentStore = useCallback(async () => {
    const slug = currentStore?.slug;
    if (!slug) return null;
    const data = await fetchStoreBySlug(slug);
    const next = mergeStoreDetail(currentStore, data);
    persistCurrentStore(next);
    setCurrentStoreState(next);
    return next;
  }, [currentStore]);

  const patchCurrentStore = useCallback((patch) => {
    setCurrentStoreState((prev) => {
      if (!prev) return prev;
      const next = {
        ...prev,
        ...patch,
        settings: {
          ...mapStoreSettings(prev),
          ...(patch.settings ?? {}),
        },
        _detailLoaded: prev._detailLoaded,
      };
      if (
        patch.customDomain !== undefined
        || patch.custom_domain !== undefined
        || patch.defaultDomain !== undefined
        || patch.url !== undefined
      ) {
        if (patch.customDomain !== undefined) {
          next.custom_domain = patch.customDomain ? [patch.customDomain] : [];
        }
        const mapped = mapApiStore(next);
        next.defaultDomain = mapped.defaultDomain;
        next.customDomain = mapped.customDomain;
        next.domain = mapped.domain;
        if (mapped.custom_domain !== undefined) next.custom_domain = mapped.custom_domain;
      }
      persistCurrentStore(next);
      return next;
    });
  }, []);

  const createStore = useCallback(async (storeDataOrName, username) => {
    const existingStores = getUserStores(username || user?.username) || [];
    if (!user?.isBeta && existingStores.length >= 1) {
      throw new Error('Seuls les utilisateurs beta peuvent creer plusieurs boutiques.');
    }

    let store;
    if (typeof storeDataOrName === 'string') {
      const name = storeDataOrName;
      const slug = name.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      store = { id: Date.now().toString(), name: name.trim(), slug, username, plan: 'free', createdAt: new Date().toISOString() };
    } else {
      const { apiPayload, ...localStoreData } = storeDataOrName;
      if (apiPayload) {
        const created = await apiCreateStore(apiPayload);
        store = { ...mapApiStore(created), _detailLoaded: true };
      } else {
        const { name, slug, ...rest } = localStoreData;
        store = { id: Date.now().toString(), name, slug, username, plan: 'free', createdAt: new Date().toISOString(), ...rest };
      }
    }
    const apiStores = JSON.parse(localStorage.getItem(API_STORES_KEY) || 'null');
    if (apiStores !== null) {
      localStorage.setItem(API_STORES_KEY, JSON.stringify([...apiStores, store]));
    } else {
      const all = JSON.parse(localStorage.getItem('kio_stores') || '[]');
      localStorage.setItem('kio_stores', JSON.stringify([...all, store]));
    }
    return store;
  }, [getUserStores, user]);

  const clearStore = useCallback(() => {
    localStorage.removeItem('kio_current_store');
    localStorage.removeItem(API_STORES_KEY);
    setCurrentStoreState(null);
    setPickingStore(true);
  }, []);

  const refreshStores = useCallback(async (username) => {
    try {
      const apiStores = await fetchUserStores();
      const storeList = Array.isArray(apiStores) ? apiStores.map(mapApiStore) : [];
      localStorage.setItem(API_STORES_KEY, JSON.stringify(storeList));
      return storeList;
    } catch {
      const all = JSON.parse(localStorage.getItem('kio_stores') || '[]');
      return username ? all.filter(s => s.username === username) : all;
    }
  }, []);

  const beginStorePicker = useCallback(() => {
    localStorage.removeItem('kio_current_store');
    setCurrentStoreState(null);
    setPickingStore(true);
  }, []);

  const updateUser = useCallback(async ({ firstName, lastName, email, phoneNumber }) => {
    await apiUpdateProfile({ firstName, lastName, email, phoneNumber });
    const apiUser = await fetchCurrentUser();
    const userData = mapApiUser(apiUser);
    localStorage.setItem('auth_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const uploadProfilePicture = useCallback(async (file) => {
    const apiUser = await apiUploadProfilePicture(file);
    const userData = mapApiUser(apiUser);
    localStorage.setItem('auth_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const refreshUser = useCallback(async () => {
    const apiUser = await fetchCurrentUser();
    const userData = mapApiUser(apiUser);
    localStorage.setItem('auth_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const createAddress = useCallback(async (payload) => {
    await apiCreateAddress(payload);
    return refreshUser();
  }, [refreshUser]);

  const updateAddress = useCallback(async (id, payload) => {
    await apiUpdateAddress(id, payload);
    return refreshUser();
  }, [refreshUser]);

  const deleteAddress = useCallback(async (id) => {
    await apiDeleteAddress(id);
    return refreshUser();
  }, [refreshUser]);

  const logout = useCallback(() => {
    apiRevokeToken().catch(() => { /* ignore */ });
    clearSessionHydrated();
    clearTokens();
    clearSheetTokens();
    clearChannelsCache();
    clearAllAppData();
    setPickingStore(false);
    setUser(null);
    setCurrentStoreState(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, currentStore, login, loginWithGoogle, signup, verifyAccount, resendVerificationCode, logout, updateUser, uploadProfilePicture, refreshUser, createAddress, updateAddress, deleteAddress, selectStore, patchCurrentStore, refreshCurrentStore, clearStore, beginStorePicker, createStore, getUserStores, refreshStores }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
